/**
 * Horizontal Facets JavaScript
 * Handles mobile dropdown functionality for horizontal facets
 */

class HorizontalFacets {
  constructor() {
    this.currentActiveTab = null;
    this.init();
  }

  init() {
    this.setupMobileDropdown();
    this.setupKeyboardNavigation();
    this.setupTabFiltering();
    this.initializeActiveStates();
    this.setupMutationObserver();
  }

  setupMobileDropdown() {
    const dropdown = document.querySelector('.horizontal-facets-dropdown');
    if (!dropdown) return;

    const trigger = dropdown.querySelector('.horizontal-facets-dropdown__trigger');
    const menu = dropdown.querySelector('.horizontal-facets-dropdown__menu');
    
    if (!trigger || !menu) return;

    // Toggle dropdown on click
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleDropdown(trigger, menu);
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!dropdown.contains(e.target)) {
        this.closeDropdown(trigger, menu);
      }
    });

    // Close dropdown on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDropdown(trigger, menu);
      }
    });
  }

  setupKeyboardNavigation() {
    const dropdown = document.querySelector('.horizontal-facets-dropdown');
    if (!dropdown) return;

    const trigger = dropdown.querySelector('.horizontal-facets-dropdown__trigger');
    const menu = dropdown.querySelector('.horizontal-facets-dropdown__menu');
    const items = menu.querySelectorAll('.horizontal-facets-dropdown__item');
    
    if (!trigger || !menu || !items.length) return;

    // Handle keyboard navigation
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleDropdown(trigger, menu);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.openDropdown(trigger, menu);
        if (items.length > 0) {
          items[0].focus();
        }
      }
    });

    // Handle arrow key navigation in menu
    items.forEach((item, index) => {
      item.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            const nextItem = items[index + 1] || items[0];
            nextItem.focus();
            break;
          case 'ArrowUp':
            e.preventDefault();
            const prevItem = items[index - 1] || items[items.length - 1];
            prevItem.focus();
            break;
          case 'Escape':
            e.preventDefault();
            this.closeDropdown(trigger, menu);
            trigger.focus();
            break;
        }
      });
    });
  }

  toggleDropdown(trigger, menu) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      this.closeDropdown(trigger, menu);
    } else {
      this.openDropdown(trigger, menu);
    }
  }

  openDropdown(trigger, menu) {
    trigger.setAttribute('aria-expanded', 'true');
    menu.style.display = 'block';
    
    // Add animation class if needed
    menu.classList.add('horizontal-facets-dropdown__menu--open');
  }

  closeDropdown(trigger, menu) {
    trigger.setAttribute('aria-expanded', 'false');
    menu.style.display = 'none';
    
    // Remove animation class if needed
    menu.classList.remove('horizontal-facets-dropdown__menu--open');
  }

  setupTabFiltering() {
    // Setup desktop tabs
    const tabs = document.querySelectorAll('.horizontal-facets-tabs__link');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleTabClick(tab);
      });
    });

    // Setup mobile dropdown items
    const dropdownItems = document.querySelectorAll('.horizontal-facets-dropdown__item');
    dropdownItems.forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleTabClick(item);
        
        // Close dropdown after selection
        const dropdown = document.querySelector('.horizontal-facets-dropdown');
        const trigger = dropdown?.querySelector('.horizontal-facets-dropdown__trigger');
        const menu = dropdown?.querySelector('.horizontal-facets-dropdown__menu');
        if (trigger && menu) {
          this.closeDropdown(trigger, menu);
        }
      });
    });
  }

  handleTabClick(element) {
    // Prevent default link behavior
    event.preventDefault();
    
    const facetTab = element.getAttribute('data-facet-tab');
    const facetValue = element.getAttribute('data-facet-value');
    
    // Store the active tab data
    this.currentActiveTab = { tabType: facetTab, tabValue: facetValue };
    
    // Force clear ALL active states first
    this.forceClearAllActiveStates();
    
    // Update visual states IMMEDIATELY when tab is clicked
    if (facetTab === 'all') {
      // Update visual states first - only "All Products" should be active
      this.updateTabActiveStates('all');
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        this.clearAllFilters();
      }, 10);
    } else if (facetTab && facetValue) {
      // Update visual states first - only the selected tab should be active
      this.updateTabActiveStates(facetTab, facetValue);
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        this.applyFilter(facetTab, facetValue);
      }, 10);
    }
  }

  updateMobileDropdownLabel(selectedElement) {
    // Check if this is a mobile dropdown item
    if (selectedElement.classList.contains('horizontal-facets-dropdown__item')) {
      const dropdown = document.querySelector('.horizontal-facets-dropdown');
      if (!dropdown) return;

      // Remove active class from all dropdown items first
      const allDropdownItems = dropdown.querySelectorAll('.horizontal-facets-dropdown__item');
      allDropdownItems.forEach(item => {
        item.classList.remove('horizontal-facets-dropdown__item--active');
      });

      // Add active class to the selected item
      selectedElement.classList.add('horizontal-facets-dropdown__item--active');

      const trigger = dropdown.querySelector('.horizontal-facets-dropdown__trigger');
      const label = trigger?.querySelector('.horizontal-facets-dropdown__label');
      
      if (trigger && label) {
        // Get the label text from the selected item
        const itemLabel = selectedElement.querySelector('.horizontal-facets-dropdown__item-label');
        if (itemLabel) {
          label.textContent = itemLabel.textContent;
        }
      }
    }
  }

  updateMobileDropdownActiveState(activeTabType, activeTabValue = null) {
    const dropdown = document.querySelector('.horizontal-facets-dropdown');
    if (!dropdown) return;

    // Remove active class from all dropdown items first
    const allDropdownItems = dropdown.querySelectorAll('.horizontal-facets-dropdown__item');
    allDropdownItems.forEach(item => {
      item.classList.remove('horizontal-facets-dropdown__item--active');
    });

    // Find and activate the correct dropdown item
    let activeItem = null;
    if (activeTabType === 'all') {
      activeItem = dropdown.querySelector('[data-facet-tab="all"]');
    } else if (activeTabType && activeTabValue) {
      activeItem = dropdown.querySelector(`[data-facet-tab="${activeTabType}"][data-facet-value="${activeTabValue}"]`);
    }

    if (activeItem) {
      activeItem.classList.add('horizontal-facets-dropdown__item--active');
      
      // Update the dropdown label
      const trigger = dropdown.querySelector('.horizontal-facets-dropdown__trigger');
      const label = trigger?.querySelector('.horizontal-facets-dropdown__label');
      
      if (trigger && label) {
        const itemLabel = activeItem.querySelector('.horizontal-facets-dropdown__item-label');
        if (itemLabel) {
          label.textContent = itemLabel.textContent;
        }
      }
    }
  }

  clearAllFilters(shouldSubmit = true) {
    const form = document.getElementById('FacetFiltersForm');
    if (!form) return;

    // Clear all checkboxes
    const checkboxes = form.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    // Clear price range inputs
    const priceInputs = form.querySelectorAll('input[type="number"]');
    priceInputs.forEach(input => {
      input.value = '';
    });

    // Clear any other filter inputs
    const allInputs = form.querySelectorAll('input, select');
    allInputs.forEach(input => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = false;
      } else if (input.type === 'number' || input.type === 'text') {
        input.value = '';
      }
    });

    // Submit the form only if requested
    if (shouldSubmit) {
      this.submitForm(form);
    }
  }

  clearAllFiltersExcept(exceptCheckbox, shouldSubmit = true) {
    const form = document.getElementById('FacetFiltersForm');
    if (!form) return;

    // Clear all checkboxes except the target one
    const checkboxes = form.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      if (checkbox !== exceptCheckbox) {
        checkbox.checked = false;
      }
    });

    // Clear price range inputs
    const priceInputs = form.querySelectorAll('input[type="number"]');
    priceInputs.forEach(input => {
      input.value = '';
    });

    // Clear any other filter inputs except our target
    const allInputs = form.querySelectorAll('input, select');
    allInputs.forEach(input => {
      if (input !== exceptCheckbox) {
        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = false;
        } else if (input.type === 'number' || input.type === 'text') {
          input.value = '';
        }
      }
    });

    // Submit the form only if requested
    if (shouldSubmit) {
      this.submitForm(form);
    }
  }

  applyFilter(paramName, value) {
    const form = document.getElementById('FacetFiltersForm');
    if (!form) return;

    // Find the checkbox for this filter value first
    let checkbox = form.querySelector(`input[name="${paramName}"][value="${value}"]`);
    
    if (!checkbox) {
      // Try alternative selector - sometimes the value might be URL encoded
      checkbox = form.querySelector(`input[name="${paramName}"][value="${encodeURIComponent(value)}"]`);
    }
    
    if (!checkbox) {
      // Try without exact value match - just by name
      checkbox = form.querySelector(`input[name="${paramName}"]`);
    }
    
    if (checkbox) {
      // Clear all other filters first (single select behavior) but preserve our target checkbox
      this.clearAllFiltersExcept(checkbox, false);
      
      // Check the selected filter
      checkbox.checked = true;
      
      // Instead of relying on form submission, build URL parameters directly
      if (checkbox.checked) {
        this.submitFilterDirectly(paramName, value);
      } else {
        checkbox.checked = true;
        // Force a change event to ensure the checkbox state is registered
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        this.submitFilterDirectly(paramName, value);
      }
    } else {
      // Fallback to direct navigation
      const tab = document.querySelector(`[data-facet-tab="${paramName}"][data-facet-value="${value}"]`);
      if (tab) {
        window.location.href = tab.href;
      }
    }
  }

  submitFilterDirectly(paramName, value) {
    // Store current active state before AJAX call
    const currentActiveTab = this.getCurrentActiveTab();
    
    // Build URL parameters directly
    const params = new URLSearchParams();
    params.set(paramName, value);
    
    // Use Dawn's AJAX filtering system
    if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.renderPage) {
      FacetFiltersForm.renderPage(params.toString());
    } else {
      // Fallback to page reload
      const baseUrl = window.location.pathname;
      const newUrl = `${baseUrl}?${params.toString()}`;
      window.location.href = newUrl;
    }
    
    // Re-apply active states after AJAX completes
    setTimeout(() => {
      this.reapplyActiveStates(currentActiveTab);
    }, 100);
  }

  submitForm(form) {
    // Store current active state before AJAX call
    const currentActiveTab = this.getCurrentActiveTab();
    
    // Use Dawn's AJAX filtering system instead of page reload
    const formData = new FormData(form);
    const params = new URLSearchParams();
    
    // Add form data to params
    for (let [key, value] of formData.entries()) {
      if (value) {
        params.append(key, value);
      }
    }

    // Check if we're clearing all filters (All Products case)
    const hasAnyFilters = params.toString().length > 0;
    
    if (!hasAnyFilters) {
      // No filters - navigate to base collection URL
      const baseUrl = window.location.pathname;
      if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.renderPage) {
        FacetFiltersForm.renderPage('');
      } else {
        window.location.href = baseUrl;
      }
    } else {
      // Has filters - use normal AJAX filtering
      if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.renderPage) {
        FacetFiltersForm.renderPage(params.toString());
      } else {
        // Fallback to page reload if FacetFiltersForm is not available
        const baseUrl = window.location.pathname;
        const newUrl = `${baseUrl}?${params.toString()}`;
        window.location.href = newUrl;
      }
    }
    
    // Re-apply active states after AJAX completes
    setTimeout(() => {
      this.reapplyActiveStates(currentActiveTab);
    }, 100);
  }

  forceClearAllActiveStates() {
    // Nuclear option - remove ALL possible active classes from ALL elements
    const allElements = document.querySelectorAll('*');
    allElements.forEach(element => {
      element.classList.remove(
        'horizontal-facets-tabs__tab--active',
        'horizontal-facets-dropdown__item--active',
        'horizontal-facets-tabs__link--active'
      );
    });
  }

  updateTabActiveStates(activeTabType, activeTabValue = null) {
    // More aggressive removal of ALL possible active states
    const allPossibleActiveElements = document.querySelectorAll(`
      .horizontal-facets-tabs__tab--active,
      .horizontal-facets-dropdown__item--active,
      .horizontal-facets-tabs__link--active,
      .horizontal-facets-tabs__tab,
      .horizontal-facets-dropdown__item,
      .horizontal-facets-tabs__link
    `);
    
    allPossibleActiveElements.forEach(element => {
      element.classList.remove(
        'horizontal-facets-tabs__tab--active',
        'horizontal-facets-dropdown__item--active',
        'horizontal-facets-tabs__link--active'
      );
    });

    // Also remove any active classes that might be on parent elements
    const allTabs = document.querySelectorAll('.horizontal-facets-tabs__tab');
    allTabs.forEach(tab => {
      tab.classList.remove('horizontal-facets-tabs__tab--active');
      const link = tab.querySelector('.horizontal-facets-tabs__link');
      if (link) {
        link.classList.remove('horizontal-facets-tabs__link--active');
      }
    });

    const allDropdownItems = document.querySelectorAll('.horizontal-facets-dropdown__item');
    allDropdownItems.forEach(item => {
      item.classList.remove('horizontal-facets-dropdown__item--active');
    });

    // Now activate the correct tab
    if (activeTabType === 'all') {
      // Activate "All Products" tab
      const allProductsTab = document.querySelector('[data-facet-tab="all"]');
      if (allProductsTab) {
        const tabElement = allProductsTab.closest('.horizontal-facets-tabs__tab');
        if (tabElement) {
          tabElement.classList.add('horizontal-facets-tabs__tab--active');
        }
        allProductsTab.classList.add('horizontal-facets-tabs__link--active');
      }
      
      // Update mobile dropdown active state
      this.updateMobileDropdownActiveState('all');
    } else if (activeTabType && activeTabValue) {
      // Activate specific tab with both tab type and value
      const activeTab = document.querySelector(`[data-facet-tab="${activeTabType}"][data-facet-value="${activeTabValue}"]`);
      if (activeTab) {
        const tabElement = activeTab.closest('.horizontal-facets-tabs__tab');
        if (tabElement) {
          tabElement.classList.add('horizontal-facets-tabs__tab--active');
        }
        activeTab.classList.add('horizontal-facets-tabs__link--active');
      }
      
      // Update mobile dropdown active state
      this.updateMobileDropdownActiveState(activeTabType, activeTabValue);
    }
  }

  getCurrentActiveTab() {
    // Find the currently active tab and return its data
    const activeTab = document.querySelector('.horizontal-facets-tabs__tab--active');
    if (activeTab) {
      const link = activeTab.querySelector('.horizontal-facets-tabs__link');
      if (link) {
        return {
          tabType: link.getAttribute('data-facet-tab'),
          tabValue: link.getAttribute('data-facet-value')
        };
      }
    }
    return null;
  }

  reapplyActiveStates(activeTabData) {
    // Re-apply active states after AJAX filtering completes
    if (activeTabData) {
      this.updateTabActiveStates(activeTabData.tabType, activeTabData.tabValue);
    }
  }

  setupMutationObserver() {
    // Watch for changes to the facets container to re-apply active states
    const facetsContainer = document.querySelector('.facets-container');
    if (!facetsContainer) return;

    let isProcessing = false;

    const observer = new MutationObserver((mutations) => {
      if (isProcessing) return;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if horizontal facets tabs were added back to DOM
          const hasHorizontalTabs = Array.from(mutation.addedNodes).some(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              return node.querySelector && node.querySelector('.horizontal-facets-tabs');
            }
            return false;
          });

          if (hasHorizontalTabs && this.currentActiveTab) {
            isProcessing = true;
            // Re-apply active states after DOM is updated
            setTimeout(() => {
              this.updateTabActiveStates(this.currentActiveTab.tabType, this.currentActiveTab.tabValue);
              isProcessing = false;
            }, 100);
          }
        }
      });
    });

    observer.observe(facetsContainer, {
      childList: true,
      subtree: true
    });
  }


  // Initialize active states based on current URL parameters
  initializeActiveStates() {
    const currentUrl = new URL(window.location.href);
    const searchParams = currentUrl.searchParams;
    
    // Check if any filter parameters are present
    let hasActiveFilter = false;
    let activeTab = null;
    let activeTabValue = null;
    
    // Check all tabs to see which one matches current URL parameters
    const allTabs = document.querySelectorAll('.horizontal-facets-tabs__link[data-facet-tab]');
    
    for (const tab of allTabs) {
      const paramName = tab.getAttribute('data-facet-tab');
      const paramValue = tab.getAttribute('data-facet-value');
      
      if (paramName && paramValue && searchParams.has(paramName) && searchParams.get(paramName) === paramValue) {
        hasActiveFilter = true;
        activeTab = paramName;
        activeTabValue = paramValue;
        break;
      }
    }
    
    if (hasActiveFilter && activeTab && activeTabValue) {
      // Store the current active tab
      this.currentActiveTab = { tabType: activeTab, tabValue: activeTabValue };
      // Activate the matching tab
      this.updateTabActiveStates(activeTab, activeTabValue);
    } else {
      // Store "All Products" as active
      this.currentActiveTab = { tabType: 'all', tabValue: null };
      // No active filters, show "All Products" as active
      this.updateTabActiveStates('all');
    }
  }

}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new HorizontalFacets();
});

// Re-initialize on page changes (for AJAX navigation)
document.addEventListener('shopify:section:load', () => {
  new HorizontalFacets();
});

// Export for potential external use
window.HorizontalFacets = HorizontalFacets;
